import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getOrganizationPolicyEditContext,
  getUserPolicyEditContext,
  toggleOrganizationPolicyVersioned,
  updateOrganizationPolicySettingsVersioned,
  toggleUserPolicy,
  updateUserPolicySettings,
  deleteOrganizationPolicySettings,
  deleteUserPolicySettings,
} from '@kirin/api-client/api/policies';
import type {
  PolicyEditContext,
  PolicyModeType,
  PolicySettingsUpdate,
} from '@kirin/api-client/contracts/policies';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';

const EDIT_CONTEXT_QUERY_KEY = 'editContext';

/**
 * Scope for editing a policy from an alert action. Organization scope edits a
 * shared org policy (with optimistic-locking / version conflict handling);
 * account scope edits the authenticated user's own policy (version-less).
 */
export type PolicyEditScope =
  | { kind: 'organization'; organizationId: string }
  | { kind: 'account' };

function editContextKey(
  scope: PolicyEditScope,
  policyKey: string,
  subPolicyKey?: string
): readonly string[] {
  const scopeKey =
    scope.kind === 'organization'
      ? ['organization', scope.organizationId]
      : ['account'];
  return [
    'policies',
    ...scopeKey,
    EDIT_CONTEXT_QUERY_KEY,
    policyKey,
    ...(subPolicyKey ? [subPolicyKey] : []),
  ] as const;
}

/**
 * Cache key prefix for all edit-context queries of a policy within a scope —
 * `['policies', …scope, 'editContext', policyKey]`. Used to invalidate the
 * dialog's cached context after a save so it refetches the freshly-persisted
 * mode/settings (every sub-policy variant matches via prefix). Without this the
 * 5-minute default `staleTime` would re-serve the pre-save value on reopen.
 *
 * The org-scope list invalidation (`['policies','organization',orgId]`) happens
 * to be a prefix of the org edit-context key, but the account-scope list
 * invalidation (`['policies','user']`) is NOT a prefix of the account
 * edit-context key (`['policies','account',…]`) — so both scopes invalidate the
 * edit-context key explicitly here.
 */
function editContextInvalidationKey(
  scope: PolicyEditScope,
  policyKey: string
): readonly string[] {
  const scopeKey =
    scope.kind === 'organization'
      ? ['organization', scope.organizationId]
      : ['account'];
  return ['policies', ...scopeKey, EDIT_CONTEXT_QUERY_KEY, policyKey] as const;
}

/**
 * Fetches the edit context for a policy from an alert action.
 * Returns policy mode, optional sub-policy setting, and version for optimistic
 * locking (organization scope only; account scope returns version 0).
 */
export function usePolicyEditContext(
  scope: PolicyEditScope,
  policyKey: string | undefined,
  subPolicyKey?: string,
  enabled = true
): ReturnType<typeof useQuery<PolicyEditContext>> {
  const hasScopeId = scope.kind === 'account' || !!scope.organizationId;
  const isEnabled = enabled && hasScopeId && !!policyKey;
  return useQuery<PolicyEditContext>({
    queryKey: editContextKey(scope, policyKey ?? '', subPolicyKey),
    queryFn: async () => {
      if (!policyKey) throw new Error('policyKey is required');
      if (scope.kind === 'account') {
        return getUserPolicyEditContext(apiClient, policyKey, { subPolicyKey });
      }
      if (!scope.organizationId) {
        throw new Error('organizationId is required');
      }
      return getOrganizationPolicyEditContext(
        apiClient,
        scope.organizationId,
        policyKey,
        { subPolicyKey }
      );
    },
    enabled: isEnabled,
  });
}

interface TogglePolicyVariables {
  policyKey: string;
  enabled: boolean;
  mode?: PolicyModeType;
  expectedVersion?: number;
  alertKey?: string;
}

/**
 * Mutation to toggle a policy mode. Organization scope uses optimistic
 * concurrency control (on 409 the caller should refetch the edit context);
 * account scope is version-less.
 */
export function useTogglePolicyVersioned(
  scope: PolicyEditScope
): ReturnType<typeof useMutation<unknown, Error, TogglePolicyVariables>> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      policyKey,
      enabled,
      mode,
      expectedVersion,
      alertKey,
    }: TogglePolicyVariables) => {
      if (scope.kind === 'account') {
        return toggleUserPolicy(apiClient, policyKey, enabled, mode, {
          source: 'alerts',
          alertKey,
        });
      }
      if (!scope.organizationId) throw new Error('Organization ID is required');
      return toggleOrganizationPolicyVersioned(
        apiClient,
        scope.organizationId,
        policyKey,
        enabled,
        { mode, expectedVersion, source: 'alerts', alertKey }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: editContextInvalidationKey(scope, variables.policyKey),
      });
      if (scope.kind === 'account') {
        queryClient.invalidateQueries({ queryKey: queryKeys.policies.user });
      } else if (scope.organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.policies.organization(scope.organizationId),
        });
      }
    },
  });
}

interface UpdateSettingsVariables {
  settingsUpdate: PolicySettingsUpdate;
  expectedVersion?: number;
  alertKey?: string;
}

/**
 * Mutation to update policy settings. Organization scope uses optimistic
 * concurrency control (on 409 the caller should refetch the edit context);
 * account scope is version-less.
 */
export function useUpdatePolicySettingsVersioned(
  scope: PolicyEditScope
): ReturnType<typeof useMutation<unknown, Error, UpdateSettingsVariables>> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      settingsUpdate,
      expectedVersion,
      alertKey,
    }: UpdateSettingsVariables) => {
      if (scope.kind === 'account') {
        return updateUserPolicySettings(apiClient, settingsUpdate, {
          source: 'alerts',
          alertKey,
        });
      }
      if (!scope.organizationId) throw new Error('Organization ID is required');
      return updateOrganizationPolicySettingsVersioned(
        apiClient,
        scope.organizationId,
        settingsUpdate,
        { expectedVersion, source: 'alerts', alertKey }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: editContextInvalidationKey(
          scope,
          variables.settingsUpdate.policyKey
        ),
      });
      if (scope.kind === 'account') {
        queryClient.invalidateQueries({
          queryKey: queryKeys.policies.settings(
            variables.settingsUpdate.policyKey
          ),
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.policies.user });
      } else if (scope.organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.policies.organizationSettings(
            scope.organizationId,
            variables.settingsUpdate.policyKey
          ),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.policies.organization(scope.organizationId),
        });
      }
    },
  });
}

interface ResetSettingsVariables {
  policyKey: string;
  settingKey?: string;
}

/**
 * Mutation to reset policy settings to defaults, scoped to organization or
 * account.
 */
export function useResetPolicySettings(
  scope: PolicyEditScope
): ReturnType<typeof useMutation<unknown, Error, ResetSettingsVariables>> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ policyKey, settingKey }: ResetSettingsVariables) => {
      if (scope.kind === 'account') {
        return deleteUserPolicySettings(apiClient, policyKey, settingKey);
      }
      if (!scope.organizationId) throw new Error('Organization ID is required');
      return deleteOrganizationPolicySettings(
        apiClient,
        scope.organizationId,
        policyKey,
        settingKey
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: editContextInvalidationKey(scope, variables.policyKey),
      });
      if (scope.kind === 'account') {
        queryClient.invalidateQueries({
          queryKey: queryKeys.policies.settings(variables.policyKey),
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.policies.user });
      } else if (scope.organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.policies.organizationSettings(
            scope.organizationId,
            variables.policyKey
          ),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.policies.organization(scope.organizationId),
        });
      }
    },
  });
}
